import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/shared";
import { api, errMsg, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";

const LEVELS = ["entry", "ic", "senior_ic", "manager", "director", "senior_director", "vp", "c_suite"];
const LIST_FIELDS = ["skills", "target_titles", "locations", "industries"];
const toText = (p) => ({ ...p, ...Object.fromEntries(LIST_FIELDS.map((k) => [k, (p[k] || []).join(", ")])) });

const F = ({ id, text, children }) => <div className="space-y-1.5"><Label htmlFor={id}>{text}</Label>{children}</div>;

const Identity = ({ user }) => (
  <div className="flex items-center gap-4 border-b pb-4" data-testid="profile-identity">
    <UserAvatar user={user} className="h-14 w-14" testid="profile-avatar" />
    <div className="min-w-0">
      <p className="truncate font-semibold" data-testid="profile-name">{user.name || user.email}</p>
      {user.prefilled_from === "linkedin" && <p className="text-xs text-slate-500" data-testid="profile-linkedin-badge">Name and photo imported from LinkedIn</p>}
    </div>
  </div>
);

export default function Profile() {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/profile").then((r) => setP(toText({ seniority: "ic", location_types: [], ...r.data }))); }, []);
  if (!p) return null;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const toggleLt = (lt) => setP({ ...p, location_types: p.location_types.includes(lt) ? p.location_types.filter((x) => x !== lt) : [...p.location_types, lt] });
  const save = async () => {
    const body = { ...p, ...Object.fromEntries(LIST_FIELDS.map((k) => [k, p[k].split(",").map((s) => s.trim()).filter(Boolean)])),
      years_experience: p.years_experience === "" || p.years_experience == null ? null : Number(p.years_experience),
      min_salary: p.min_salary === "" || p.min_salary == null ? null : Number(p.min_salary) };
    try { await api.put("/profile", body); toast.success("Profile saved — match scores updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Matching inputs" title="Profile"><Button onClick={save} className="rounded-full" data-testid="profile-save-button">Save profile</Button></PageHeader>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <Panel className="space-y-4" testid="profile-basics">
          <Identity user={user} />
          <F id="headline" text="Professional headline"><Input id="headline" value={p.headline || ""} onChange={set("headline")} data-testid="profile-headline-input" /></F>
          <div className="grid gap-4 sm:grid-cols-2">
            <F id="seniority" text="Current level"><Select value={p.seniority} onValueChange={(v) => setP({ ...p, seniority: v })}><SelectTrigger className="bg-white" data-testid="profile-seniority-select"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{LEVELS.map((l) => <SelectItem key={l} value={l}>{label(l)}</SelectItem>)}</SelectContent></Select></F>
            <F id="years" text="Years of experience"><Input id="years" type="number" min={0} value={p.years_experience ?? ""} onChange={set("years_experience")} data-testid="profile-years-input" /></F>
          </div>
          <F id="skills" text="Skills (comma separated)"><Textarea id="skills" rows={3} value={p.skills} onChange={set("skills")} data-testid="profile-skills-input" /></F>
          <F id="titles" text="Target titles (comma separated)"><Input id="titles" value={p.target_titles} onChange={set("target_titles")} data-testid="profile-titles-input" /></F>
          <F id="industries" text="Preferred industries"><Input id="industries" value={p.industries} onChange={set("industries")} data-testid="profile-industries-input" /></F>
        </Panel>
        <Panel className="space-y-4" testid="profile-preferences">
          <F id="minsal" text="Minimum base salary (USD)"><Input id="minsal" type="number" min={0} step={5000} value={p.min_salary ?? ""} onChange={set("min_salary")} data-testid="profile-min-salary-input" /></F>
          <F id="locations" text="Preferred locations (comma separated)"><Input id="locations" value={p.locations} onChange={set("locations")} data-testid="profile-locations-input" /></F>
          <div><Label>Work modes</Label><div className="mt-2 flex gap-5">{["remote", "hybrid", "onsite"].map((lt) => (
            <label key={lt} className="flex items-center gap-2 text-sm capitalize"><Checkbox checked={p.location_types.includes(lt)} onCheckedChange={() => toggleLt(lt)} data-testid={`profile-lt-${lt}`} />{lt}</label>))}</div></div>
          <F id="linkedin" text="LinkedIn profile URL (entered manually)"><Input id="linkedin" value={p.linkedin_url || ""} onChange={set("linkedin_url")} data-testid="profile-linkedin-input" /></F>
          <F id="phone" text="Phone (encrypted at rest)"><Input id="phone" value={p.phone || ""} onChange={set("phone")} data-testid="profile-phone-input" /></F>
        </Panel>
        <Panel className="lg:col-span-2" testid="profile-resume">
          <F id="resume" text="Resume text (encrypted at rest; used by JobPedestal Intelligence and never shown to recruiters)"><Textarea id="resume" rows={12} value={p.resume_text || ""} onChange={set("resume_text")} data-testid="profile-resume-input" /></F>
        </Panel>
      </div>
    </div>
  );
}
